import React from 'react';
import { beforeAfterGallery } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import Reveal from './motion/Reveal';

const BeforeAfterGallery = () => {
  const { t } = useLanguage();

  // Check if image exists for this gallery item
  const hasImage = (id) => {
    return id === 2 || id === 4 || id === 5 || id === 7 || id === 8; // All except Eyebrow Lamination/Henna
  };

  const isComposite = (id) => {
    return hasImage(id); // All remaining imaged items are composite images
  };

  return (
    <section id="gallery" className="py-24 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Reveal className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-4xl font-normal text-black">
            {t.gallery.title}
          </h2>
          <div className="w-16 h-px bg-black mx-auto"></div>
          <p className="text-lg text-gray-700">
            {t.gallery.subtitle}
          </p>
        </Reveal>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {beforeAfterGallery.map((item) => (
            <div 
              key={item.id}
              className="group relative overflow-hidden bg-white aspect-[4/3] border-2 border-black"
            >
              {hasImage(item.id) ? (
                isComposite(item.id) ? (
                  /* Composite Image - Multiple Views */
                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                    <img 
                      src={`/images/gallery/gallery-${item.id}-composite.jpg`}
                      alt={`${item.category} - Results`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  /* Real Before/After Images */
                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                    <div className="w-full h-full grid grid-cols-2 gap-0">
                      <div className="relative">
                        <img 
                          src={`/images/gallery/gallery-${item.id}-before.jpg`}
                          alt={`${item.category} - Before`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-3 left-3 bg-black bg-opacity-80 text-white text-xs font-medium px-3 py-1">
                          BEFORE
                        </div>
                      </div>
                      <div className="relative">
                        <img 
                          src={`/images/gallery/gallery-${item.id}-after.jpg`}
                          alt={`${item.category} - After`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-3 right-3 bg-black bg-opacity-80 text-white text-xs font-medium px-3 py-1">
                          AFTER
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                /* Placeholder for images not yet uploaded */
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center space-y-3 p-6">
                    <div className="flex items-center justify-center space-x-4">
                      <div className="w-20 h-20 bg-gray-300"></div>
                      <div className="text-gray-400 font-bold">→</div>
                      <div className="w-20 h-20 bg-gray-300"></div>
                    </div>
                    <p className="text-gray-600 text-xs font-medium">Before & After</p>
                    <p className="text-gray-500 text-xs">{item.category}</p>
                  </div>
                </div>
              )}

              {/* Overlay with description - only show on hover for real images */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-80 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center space-y-2 px-6">
                  <h3 className="text-lg font-normal text-white">
                    {item.category}
                  </h3>
                  <p className="text-sm text-gray-300">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 text-center">
          <p className="text-xs text-gray-500 max-w-2xl mx-auto">
            Results may vary. Individual results are not guaranteed. Before and after photos are of actual patients with their consent.
          </p>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterGallery;
